<template>
	<EditorShell>
		<!-- Header -->
		<CLayoutEditorHeader
			:isLoading="isLoading"
			:headline="template ? template.name : null"
			:saveButton="$t('templates.save')"
			@back:click="$router.back()"
			@save:click="updateTemplate"
		>
			<EditorSaveChangesPrompt :isVisible="hasChanged" slot="middle" />
		</CLayoutEditorHeader>

		<!-- Wrapper -->
		<JBox class="s-px-4 s-max-w-5xl s-mx-auto">
			<!-- Locale switcher and preview -->
			<JFlex class="s-justify-between s-mt-4 s-h-10">
				<JBox>
					<JSelect :allowSelectNothing="false" v-model="selectedLocale" :options="localeOptions" @input="handleSwitchLocale" />
				</JBox>

				<!-- Preview switcher -->
				<EditorPreviewSwitcher
					:showPreview="showPreview"
					:label="$t('templates.contentJsonString')"
					@preview:toggle="showPreview = $event"
					@preview:open="renderPreview"
					@render:click="renderPreview"
					v-model="previewVariables"
				/>
			</JFlex>

			<!-- Editor and rendered preview -->
			<JBox>
				<JFlex class="s-bg-white s-rounded-md s-border s-border-gray-300 s-px-5 s-pb-5 s-pt-3 s-mt-1 s-mb-4">
					<!-- Editor -->
					<JBox class="s-w-full s-z-0">
						<JFlex>
							<JHeadline class="s-font-semibold s-text-gray-700 s-mb-2 s-text-xl">{{ $t("templates.content") }}</JHeadline>
						</JFlex>
						<CRichTextEditor :height="500" v-model="template.localizedHtml" />
						<JText class="s-mt-2 s-text-gray-600 s-text-xs">{{ $t("templates.variablesPopulate") }}</JText>
					</JBox>

					<!-- Preview -->
					<Transition enter-active-class="animated fadeIn faster">
						<JBox v-if="showPreview" class="s-w-full s-pl-4">
							<JHeadline class="s-font-semibold s-text-gray-700 s-mb-2 s-text-xl">{{
								$t("templates.renderedNotification")
							}}</JHeadline>
							<JBox class="s-border s-border-gray-300 s-shadow s-p-2 s-rounded" v-if="!isPreviewLoading" v-html="previewResult" />
							<JSpinner variant="indigo" type="dots" :delay="100" v-else />
						</JBox>
					</Transition>
				</JFlex>
			</JBox>

			<!-- Document Settings -->
			<EditorAccordion :headline="$t('templates.document_settings')" class="s-mb-4">
				<EditorDocumentSettings v-model="template" />
			</EditorAccordion>

			<!-- Delete template -->
			<CDialogProvider
				:headline="$t('templates.delete_template')"
				:description="$t('templates.delete_dialog')"
				:cancelButton="$t('templates.continueEditing')"
				:confirmButton="$t('templates.delete_template')"
				@confirm="deleteTemplate"
			>
				<JFlex class="s-justify-end" slot-scope="{ openDialog }">
					<JButton variant="error" @click="openDialog">{{ $t("templates.delete_template") }} </JButton>
				</JFlex>
			</CDialogProvider>
		</JBox>
		<!-- Debug Console -->
		<EditorDebugConsole :errors="errors" />
	</EditorShell>
</template>

<script>
import EditorShell from "../components/EditorShell"
import EditorAccordion from "../components/EditorAccordion"
import EditorSaveChangesPrompt from "../components/EditorSaveChangesPrompt"
import EditorPreviewSwitcher from "../components/EditorPreviewSwitcher"
import EditorDocumentSettings from "../components/EditorDocumentSettings"
import EditorDebugConsole from "../components/EditorDebugConsole"
import { preventBrowserBackButton } from "../utils/preventBrowserBackButton"
import { addHintToLiquidErrorString } from "../utils/addHintToLiquidErrorString"
import isEqual from "lodash.isequal"
import { deepClone } from "@/utils/"
import api from "@/api/api"
import { query, mutation } from "../api/queries"

export default {
	components: {
		EditorShell,
		EditorAccordion,
		EditorSaveChangesPrompt,
		EditorPreviewSwitcher,
		EditorDebugConsole,
		EditorDocumentSettings
	},

	props: {
		id: {
			type: String
		}
	},

	data() {
		return {
			isLoading: false,
			template: {},
			originalTemplate: {},
			selectedLocale: "en-US",

			showPreview: false,
			isPreviewLoading: false,
			previewResult: null,
			previewVariables: null,

			errors: []
		}
	},

	computed: {
		hasChanged() {
			return !isEqual(this.template, this.originalTemplate)
		},

		localeOptions() {
			return this.$store?.state?.settings?.localesDictionary
		}
	},

	methods: {
		async fetchTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: query.fetchNotificationTemplate,
				locale: this.selectedLocale,
				variables: {
					where: {
						id: this.id ? this.id : null
					}
				}
			})
			this.isLoading = false

			if (!res?.data?.templates?.list?.[0]) {
				this.setErrors(res?.errors?.map(item => item?.message) || [this.$t("templates.noTemplate")])
				return
			}

			this.template = res?.data?.templates?.list?.[0] ?? {}
			this.originalTemplate = deepClone(res?.data?.templates?.list?.[0] ?? {})
		},

		async deleteTemplate() {
			if (this.isLoading) return
			this.setErrors()

			// Delete the template
			this.isLoading = true
			const res = await api({
				query: mutation.deleteTemplate,
				variables: { templateId: this.id ? this.id : null }
			})

			if (res.data?.deleteTemplate?.status !== "success") {
				this.setErrors(res?.data?.deleteTemplate?.errors || res?.errors?.map(item => item?.message))
				this.isLoading = false
				return
			}

			// Clean up localization - delete all keys associated to this template
			// Ignore errors of l10nRes because if it fails, there is no recovery path anyways
			// It is not highly important to delete localization keys
			await api({
				query: mutation.deleteAllL10n,
				variables: {
					key: `templates-${this.id}-html`
				}
			})
			this.isLoading = false

			this.$router.push({ name: "ViewTemplatesHome" })
		},

		async updateTemplate() {
			if (this.isLoading) return
			this.setErrors()

			this.isLoading = true
			const res = await api({
				query: mutation.upsertL10n,
				variables: {
					l10n: {
						locale: this.selectedLocale,
						key: `templates-${this.id}-html`,
						value: this.template?.localizedHtml
					}
				}
			})

			if (res?.data?.upsertL10n?.status !== "success") {
				this.setErrors(res?.data?.upsertL10n?.errors || res?.errors?.map(item => item?.message))
				this.isLoading = false
				return
			}

			const res2 = await api({
				query: mutation.updateNotificationTemplate,
				variables: {
					templateId: this.id ? this.id : null,
					input: {
						name: this.template?.name,
						description: this.template?.description,
						logicalId: this.template?.logicalId,
					}
				}
			})
			this.isLoading = false

			if (res2?.data?.updateNotificationTemplate?.status !== "success") {
				this.setErrors(res?.data?.updateNotificationTemplate?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.originalTemplate = deepClone(this.template)
		},

		async renderPreview() {
			if (this.isPreviewLoading) return
			this.setErrors()

			this.isPreviewLoading = true
			const res = await api({
				query: query.renderHtml,
				variables: {
					html: this.template?.localizedHtml ?? "",
					input: {
						contentJsonString: this.previewVariables ? this.previewVariables : undefined,
						name: "data"
					}
				}
			})
			this.isPreviewLoading = false

			if (res?.data?.renderHtml?.status !== "success") {
				this.setLiquidErrors(res?.data?.renderHtml?.errors || res?.errors?.map(item => item?.message))
				return
			}

			this.previewResult = res?.data?.renderHtml?.value
		},

		handleSwitchLocale() {
			this.fetchTemplate()
		},

		setLiquidErrors(errors = []) {
			const output = errors.map(
				item => addHintToLiquidErrorString({ message: item, templateSource: this?.template?.localizedHtml }) // returns {message, hint} object
			)
			this.errors = output
		},

		setErrors(errors = [], { hint = "" } = {}) {
			this.errors = errors?.map(err => ({ message: err, hint }))
		}
	},

	created() {
		this.fetchTemplate()
	},

	beforeRouteLeave(to, from, next) {
		preventBrowserBackButton({ next, showDialog: this.hasChanged })
	}
}
</script>
